import React, { useRef, useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import PropTypes from 'prop-types';
import { Box, Grow, IconButton, Paper, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import saleforce from '../images/salesforceDest.png';
import oracle from '../images/oracleDest.png';
import sql from '../images/sqlDest.png';
import hubspot from '../images/hubspotdes.png';
import { useThemeContext } from '../../Context/ThemeContext';
import { useTheme } from '@mui/material/styles';
import salesforceIcon from '../images/salesforce.png';
import oracleIcon from '../images/oracle.png';
import hubspotIcon from '../images/hubspot.png';
import sqlIcon from '../images/sql.png';
import githubIcon from '../images/github.png';
import github from '../images/githubDest.png';

const DestinationNode = ({ id, data, deleteNode }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isHandleHover, setIsHandleHover] = useState(false);
  const hoverTimeout = useRef(null);

  const { darkMode } = useThemeContext();
  const muiTheme = useTheme();

  const handleMouseEnter = () => {
    hoverTimeout.current = setTimeout(() => setIsHovered(true), 400);
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimeout.current);
    setIsHovered(false);
    setIsHandleHover(false);
  };

  const [mainLabel, typeLabel] = data.label.includes('(')
    ? data.label.split(' (')
    : [data.label, ''];

  const displayType = typeLabel.replace(')', '').trim();

  const icon =
    mainLabel === 'Salesforce' ? saleforce : mainLabel === 'HubSpot' ? hubspot : mainLabel === 'GitHub' ? github : mainLabel === 'SQL' ? sql : oracle;
  const eachIcon =
    mainLabel === 'Salesforce' ? salesforceIcon : mainLabel === 'HubSpot' ? hubspotIcon : mainLabel === 'GitHub' ? githubIcon : mainLabel === 'SQL' ? sqlIcon : oracleIcon;

  const showOverlay = isHovered && !isHandleHover;

  return (
    <Box
      sx={{
        width: 65,
        height: 65,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '8px',
        backgroundColor: '#fff',
        boxShadow:
          mainLabel === 'Oracle'
            ? isHovered
              ? '0 8px 20px rgba(174, 2, 2, 0.45)'
              : '0 4px 14px rgba(174, 2, 2, 0.35)'
            : isHovered
            ? '0 8px 20px rgba(84, 155, 222, 0.55)'
            : '0 4px 14px rgba(84, 155, 222, 0.45)',
        zIndex: isHovered ? 100 : 1,
        transition: 'box-shadow 0.25s ease, transform 0.25s ease',
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 16px rgba(0, 0, 0, 0.3)',
        },
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <img
        src={icon}
        alt="Node"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          borderRadius: '6px',
        }}
      />

      <Grow
        in={showOverlay}
        timeout={{ enter: 300, exit: 250 }}
        style={{ transitionDelay: showOverlay ? '100ms' : '0ms' }}
      >
        <Paper
          elevation={4}
          sx={{
            position: 'absolute',
            top: -8,
            left: -12,
            minWidth: 170,
            minHeight: 50,
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            borderRadius: 1,
            bgcolor: darkMode ? muiTheme.palette.background.paper : '#fff',
            border: `1.5px solid ${darkMode ? '#3a8eff' : '#549bde'}`,
            zIndex: 200,
            transition: 'opacity 0.15s',
            pointerEvents: 'auto',
            boxShadow: '0 6px 12px rgba(0, 0, 0, 0.25)',
            '&:hover': { boxShadow: '0 8px 16px rgba(0, 0, 0, 0.3)' },
          }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 1,
              bgcolor: darkMode ? '#20223c' : '#f5f6fa',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2,
            }}
          >
            <img src={eachIcon} alt="Node" width={34} height={34} />
          </Box>
          <Box sx={{ ml: 1 }}>
            <Typography
              sx={{
                color: darkMode ? '#b0b0b0' : '#757575',
                fontSize: '0.75rem',
                marginBottom: '0.25rem',
              }}
            >
              {displayType}
            </Typography>
            <Typography
              sx={{
                color: darkMode ? '#fff' : '#212121',
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              {mainLabel}
            </Typography>
          </Box>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              deleteNode(id, data.label);
            }}
            size="small"
            sx={{
              position: 'absolute',
              top: 6,
              right: 6,
              background: darkMode ? '#353535' : '#fff',
              color: muiTheme.palette.error.main,
              width: '1.25rem',
              height: '1.25rem',
              borderRadius: '50%',
              boxShadow: 1,
              zIndex: 2,
              '&:hover': {
                background: darkMode
                  ? muiTheme.palette.error.dark
                  : muiTheme.palette.error.dark,
                color: '#fff',
              },
            }}
          >
            <Close sx={{ fontSize: '0.75rem' }} />
          </IconButton>
        </Paper>
      </Grow>

      {(!isHovered || isHandleHover) && (
        <Handle
          type="target"
          position={Position.Left}
          onMouseEnter={() => setIsHandleHover(true)}
          onMouseLeave={() => setIsHandleHover(false)}
          style={{ zIndex: 300 }}
        />
      )}
      {(!isHovered || isHandleHover) && (
        <Handle
          type="source"
          position={Position.Right}
          onMouseEnter={() => setIsHandleHover(true)}
          onMouseLeave={() => setIsHandleHover(false)}
          style={{ zIndex: 300 }}
        />
      )}
    </Box>
  );
};

DestinationNode.propTypes = {
  id: PropTypes.string.isRequired,
  data: PropTypes.shape({
    label: PropTypes.string.isRequired,
  }).isRequired,
  deleteNode: PropTypes.func.isRequired,
};

export default DestinationNode;
